import { css, cx } from "@emotion/css";
import { Edit, FileText, MoreVertical, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import { PrimaryButton } from "src/components/Default/LekUI/Button";
import { Menu } from "src/components/Default/LekUI/Menu";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import DefaultActions from "src/components/NewComponents/menuActions/DefaultActions";
import { useFolderItemActions } from "src/components/NewComponents/menuActions/useFolderItemActions";
import formatHtmlTag from "src/helpers/formatHtmlTag";
import { useDeleteUserNote } from "src/hooks/useFolders";

export interface NoteRow {
	id: string;
	name?: string;
	note?: string;
	createdAt?: string;
	folderId?: string;
	folderItemId?: string | number;
	[key: string]: unknown;
}

interface NoteCardViewProps {
	row: NoteRow;
	folderId: string;
	className?: string;
}

export function NoteCardView({ row, folderId, className }: NoteCardViewProps) {
	const { t } = useTranslation();
	const history = useHistory();
	const { openModal } = useModals();
	const folderActions = useFolderItemActions(row, "note");
	const { mutateAsync: deleteNote } = useDeleteUserNote();

	const formattedDate = row.createdAt
		? new Date(row.createdAt).toLocaleDateString("pl-PL")
		: null;

	const preview = row.note ? formatHtmlTag(row.note) : "";

	const onWatch = () => {
		history.push(
			generatePath(routing.paths.previewNote, { id: folderId, noteId: row.id }),
		);
	};

	const onEdit = () => {
		history.push(
			generatePath(routing.paths.editNote, { id: folderId, noteId: row.id }),
		);
	};

	const onDelete = () => {
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.note.title", "Usunąć notatkę?"),
			description: t(
				"modals.deleteItem.note.description",
				"Notatka zostanie trwale usunięta. Tej operacji nie można cofnąć.",
			),
			confirmText: t("modals.deleteItem.note.confirm", "Usuń notatkę"),
			itemLabel: row.name,
			onDelete: () => deleteNote(row.id),
		});
	};

	return (
		<div
			className={cx(
				className,
				"content-box flex flex-col gap-16 justify-between",
				css`min-height: 150px;`,
			)}
		>
			<div className="flex flex-col gap-16">
				<div className="flex items-start justify-between">
					{formattedDate && (
						<div className="border-radius-8 p-8 size-12 w-fit-content bg text-primary">
							{formattedDate}
						</div>
					)}
					<Menu
						icon={<MoreVertical className="icon-small" />}
						buttonTestId={`note-menu-button-${row.id}`}
						Actions={
							<DefaultActions
								defaultItems={[
									{
										title: t("commands.edit", "Edytuj"),
										onClick: onEdit,
										Icon: <Edit className="icon-small" />,
										testId: `note-edit-${row.id}`,
									},
									row.folderId && folderActions,
									{
										title: t("commands.delete", "Usuń"),
										onClick: onDelete,
										Icon: <Trash2 className="icon-small icon-error" />,
										className: "list-item-error",
										testId: `note-delete-${row.id}`,
									},
								]}
							/>
						}
					/>
				</div>
				<div className="flex items-center gap-8">
					<FileText className="icon-small" />
					<Tooltip title={row.name}>
						<h3 className="text-ellipsis-1 m-0 size-16">
							{row.name || t("note.untitled", "Notatka bez tytułu")}
						</h3>
					</Tooltip>
				</div>
				<p className="text-ellipsis-2 m-0 size-14 text-black-80">
					{preview || "\u00A0"}
				</p>
			</div>
			<div className="flex justify-end">
				<PrimaryButton onClick={onWatch}>
					{t("commands.watch", "Zobacz")}
				</PrimaryButton>
			</div>
		</div>
	);
}
